import React from "react";

/**
 * MilestoneRibbon — celebratory pill on the homepage hero.
 *
 * Owner directive (2026-06-21): call out the June 2026 milestone of
 * 18,000+ customers nationwide for a limited window (through August 31,
 * 2026) so returning visitors see the celebration in context, then the
 * ribbon auto-hides and the hero returns to the evergreen storytelling.
 *
 * Brand palette (STRICT): purple `#663399` background + green `#78D33C`
 * accent dot. Absolutely no dark navy or black — see project guidelines.
 * The pill uses a subtle inset shadow for depth without dropping onto
 * a black backdrop; text is white for contrast against the purple.
 *
 * Behavior:
 *   • Renders `null` (nothing) if today (JST) > 2026-08-31.
 *   • data-testid="homepage-milestone-ribbon" so tests + admin can
 *     confirm/deny presence.
 *   • Bilingual — reads `lang` from the caller.
 */
const CUTOFF_ISO = "2026-08-31T23:59:59+09:00";

export default function MilestoneRibbon({ lang = "en" }) {
  // Client-side date check — the ribbon self-disables past its window,
  // so no cron / no server flag needed. Fail-open: if the current
  // clock is somehow < 2026, we still show it (the milestone happened,
  // dates should never go backwards).
  const now = Date.now();
  const cutoff = new Date(CUTOFF_ISO).getTime();
  if (now > cutoff) return null;

  const isJa = lang === "ja";
  const label = isJa
    ? "2026年6月、日本全国で18,000名超のお客様に到達 ─ 大きな節目"
    : "June 2026 — 18,000+ customers across Japan · a major milestone";

  return (
    <div
      data-testid="homepage-milestone-ribbon"
      role="note"
      className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[11.5px] md:text-[12px] font-semibold tracking-tight leading-none shadow-[inset_0_-1px_0_rgba(255,255,255,0.15),0_1px_0_rgba(0,0,0,0.05)]"
      style={{
        backgroundColor: "#663399",
        color: "#FFFFFF",
      }}
    >
      <span
        aria-hidden="true"
        className="inline-block h-2 w-2 rounded-full"
        style={{ backgroundColor: "#78D33C" }}
      />
      <span>{label}</span>
    </div>
  );
}
